import { Link, NavLink } from 'react-router';
import { BrandMark } from '../../components/BrandMark.js';
import { AccountMenu } from './AccountMenu.js';
import { MobileMenu } from './MobileMenu.js';

function navLinkClassName({ isActive }: { isActive: boolean }): string {
  return isActive ? 'public__nav-link public__nav-link--active' : 'public__nav-link';
}

/**
 * Logo (reuses `<BrandMark />`, same mechanism `/apply` already uses — no
 * new logo plumbing) plus the reference screenshot's four nav links.
 *
 * The brand mark links back to `/` on every page under `PublicLayout`
 * (homepage, listing detail pages, compliance stubs) — the conventional
 * "click the logo to go home" affordance every other page here was missing.
 *
 * "Rental Finder" points at `/listings` (`RentalFinder.tsx`, the filter+map
 * page); "New Rentals" points at `/new-rentals` (`NewRentals.tsx`, the
 * newest listings grouped by city) — both real now. "Hire a Property
 * Manager" points at `/hire-a-property-manager` (`HirePropertyManager.tsx`),
 * last in the nav per direct request. "My Account" is `<AccountMenu />`, a
 * dropdown to the two real portal doors (resident and owner), since there's
 * no single login this link could point at directly. "Help Center" has no
 * equivalent anywhere in the app yet, so it stays inert.
 *
 * "Powered by B1 Property" sits under the logo, per direct request — plain
 * text, not a second `useBrand()` read, since this exact wording is what
 * was asked for, independent of whatever `company.name` the logo's
 * alt-text/fallback resolves to.
 *
 * `<MobileMenu />` is rendered unconditionally and hides itself via CSS
 * except below the breakpoint where `.public__nav`/`.public__header-actions`
 * hide outright (see styles.css's responsive floor) — a hamburger standing
 * in for both once there's no room for the full row.
 */
export function PublicHeader() {
  return (
    <header className="public__header">
      <Link to="/" className="public__brand">
        <BrandMark />
        <span className="public__brand-tagline">Powered by B1 Property</span>
      </Link>
      <nav className="public__nav" aria-label="Listings">
        <NavLink to="/new-rentals" className={navLinkClassName}>
          New Rentals
        </NavLink>
        <NavLink to="/listings" className={navLinkClassName}>
          Rental Finder
        </NavLink>
        <NavLink to="/hire-a-property-manager" className={navLinkClassName}>
          Hire a Property Manager
        </NavLink>
      </nav>
      <div className="public__header-actions">
        <span className="public__nav-link public__nav-link--inert">Help Center</span>
        <AccountMenu />
      </div>
      <MobileMenu />
    </header>
  );
}
